/*
 * Project: bellcloud
 * File: apps/frontend/src/features/guide/GuideProvider.tsx
 * Description: 앱 전역 가이드 상태 Provider. react-joyride 를 단 한 번만 렌더링한다.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Joyride, EVENTS, type EventData, type Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme';
import { GuideContext } from './useGuide';
import { buildGuideSteps } from './guideSteps';
import { markGuideCompleted } from './guideStorage';
import type { GuideContextValue, GuideKey } from './types';

interface GuideProviderProps {
  children: React.ReactNode;
}

export const GuideProvider: React.FC<GuideProviderProps> = ({ children }) => {
  const { theme } = useTheme();
  const { t } = useTranslation('guide');

  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [currentGuideKey, setCurrentGuideKey] = useState<string | null>(null);

  const stopGuide = useCallback(() => {
    setRun(false);
    setCurrentGuideKey(null);
    setSteps([]);
  }, []);

  const startGuide = useCallback(
    (guideKey: string) => {
      const allSteps = buildGuideSteps(key => t(key));
      const guide = allSteps[guideKey as GuideKey];

      // 예외 1: 해당 guideKey 가 없으면 경고만 남기고 실행하지 않는다.
      if (!guide) {
        console.warn(`[Guide] 등록되지 않은 guideKey 입니다: "${guideKey}"`);
        return;
      }

      // 페이지가 동적 타겟(예: 접힌 목록/디바이스 리스트)을 미리 펼칠 수 있도록 시작 신호 전파.
      window.dispatchEvent(new CustomEvent('bellcloud:guide:start', { detail: { guideKey } }));

      // 예외 2·4: 화면에 존재하는 target 만 남긴다 (권한/조건부 미표시 요소로 인한 중단 방지).
      const collectVisible = () =>
        guide.filter(step =>
          typeof step.target !== 'string' ? true : Boolean(document.querySelector(step.target))
        );

      const begin = (steps: typeof guide) => {
        if (steps.length === 0) {
          console.warn(`[Guide] 화면에 표시할 target 이 없어 가이드를 건너뜁니다: "${guideKey}"`);
          return;
        }
        setSteps(steps);
        setCurrentGuideKey(guideKey);
        setRun(true);
      };

      // 비동기로 나타나는 타겟(펼쳐진 뒤 fetch 되는 디바이스 목록 등)을 기다리기 위해
      // 모든 step 이 나타나거나, 일정 시간 뒤 안정될 때까지 잠깐 폴링한 후 시작한다.
      let attempts = 0;
      let lastCount = -1;
      let stableTicks = 0;
      const tick = () => {
        const visible = collectVisible();
        stableTicks = visible.length === lastCount ? stableTicks + 1 : 0;
        lastCount = visible.length;

        const allPresent = visible.length === guide.length;
        const settled = stableTicks >= 2 && attempts >= 6; // 최소 ~0.7s 대기 후 안정되면 시작
        const timedOut = attempts >= 15; // 최대 ~1.8s

        if (allPresent || settled || timedOut) {
          begin(visible);
          return;
        }
        attempts += 1;
        window.setTimeout(tick, 120);
      };
      tick();
    },
    [t]
  );

  // 투어 종료(완료·건너뛰기·닫기) 시 완료 상태 저장 후 정리
  const handleEvent = useCallback(
    (data: EventData) => {
      if (data.type === EVENTS.TOUR_END) {
        if (currentGuideKey) {
          markGuideCompleted(currentGuideKey);
          // 가이드가 펼쳤던 동적 영역을 페이지가 원래대로 되돌릴 수 있도록 종료 신호 전파.
          window.dispatchEvent(
            new CustomEvent('bellcloud:guide:end', { detail: { guideKey: currentGuideKey } })
          );
        }
        stopGuide();
      }
    },
    [currentGuideKey, stopGuide]
  );

  const value = useMemo<GuideContextValue>(
    () => ({
      startGuide,
      stopGuide,
      isGuideRunning: run,
      currentGuideKey,
    }),
    [startGuide, stopGuide, run, currentGuideKey]
  );

  return (
    <GuideContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        locale={{
          back: t('back'),
          close: t('close'),
          last: t('last'),
          next: t('next'),
          nextWithProgress: t('next_with_progress'),
          skip: t('skip'),
        }}
        options={{
          // 기존 BELLCLOUD 포인트 컬러
          primaryColor: theme.colors.button.bg.primary.default,
          backgroundColor: theme.colors.surface.layout.overlay,
          arrowColor: theme.colors.surface.layout.overlay,
          textColor: theme.colors.text.primary,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          // 모달(1040)·셀렉트/로딩(9999)보다 위에 떠서 항상 보이도록 설정
          zIndex: 10000,
          showProgress: true,
          // 이전 / 닫기 / 다음·완료 / 건너뛰기 버튼 노출
          buttons: ['back', 'close', 'primary', 'skip'],
          // 닫기(X) 클릭 시 투어 전체 종료
          closeButtonAction: 'skip',
        }}
        styles={{
          tooltipContainer: {
            textAlign: 'left',
          },
          // 문구의 \n 줄바꿈이 그대로 적용되도록 pre-line 처리
          tooltipContent: {
            whiteSpace: 'pre-line',
          },
        }}
      />
    </GuideContext.Provider>
  );
};
